import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Loader from '../../../components/common/Loader';
import { assignManager, getReportingUsers } from '../../../services/adminService';
import { usePermissions } from '../../../context/PermissionsContext';
import '../../../styles/orgChart.css';

function OrgChart({ onAssignManager, showAssignManagerButton = false }) {
  const { permissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0, panX: 0, panY: 0 });
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [imgFailed, setImgFailed] = useState(() => new Set());
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [unassignedQuery, setUnassignedQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [draggingUser, setDraggingUser] = useState(null);
  const [dropHoverId, setDropHoverId] = useState(null);
  const [confirmMove, setConfirmMove] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSaving, setConfirmSaving] = useState(false);

  const [topLevelOpen, setTopLevelOpen] = useState(false);
  const [topLevelQuery, setTopLevelQuery] = useState('');
  const [topLevelSelectedId, setTopLevelSelectedId] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getReportingUsers();
      if (res?.success) {
        const list = Array.isArray(res?.data?.users) ? res.data.users : Array.isArray(res?.data) ? res.data : [];
        setUsers(list);
        const initialExpanded = new Set(list.map((u) => String(u.id)));
        setExpanded(initialExpanded);
      } else {
        toast.error(res?.message || res?.error_msg || 'Failed to load users');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openTopLevelModal = () => {
    if (!permissions?.roles_and_access_user_edit) {
      toast.warning("You don't have permission to assign managers");
      return;
    }
    setTopLevelQuery('');
    setTopLevelSelectedId('');
    setTopLevelOpen(true);
  };

  const topLevelCandidates = useMemo(() => {
    const q = (topLevelQuery || '').trim().toLowerCase();
    const list = users;
    if (!q) return list;
    return list.filter((u) => {
      const name = (u?.name || '').toString().toLowerCase();
      const email = (u?.email || '').toString().toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [users, topLevelQuery]);

  const selectedTopLevelUser = useMemo(
    () => users.find((u) => String(u.id) === String(topLevelSelectedId)),
    [users, topLevelSelectedId]
  );

  const startDrag = (user) => {
    if (!user) return;
    setDraggingUser({ id: String(user.id), name: user.name, email: user.email });
  };

  const requestMove = ({ reporteeId, supervisorId, isOrgRoot }) => {
    if (!permissions?.roles_and_access_user_edit) {
      toast.warning("You don't have permission to assign managers");
      return;
    }
    if (reporteeId == null) return;
    if (String(reporteeId) === String(supervisorId)) {
      toast.warning('A user cannot be their own manager');
      return;
    }
    const reportee = users.find((u) => String(u.id) === String(reporteeId));
    const manager = supervisorId == null ? null : users.find((u) => String(u.id) === String(supervisorId));
    setConfirmMove({
      reportee_id: Number(reporteeId),
      supervisor_id: supervisorId == null ? null : Number(supervisorId),
      is_org_root: Boolean(isOrgRoot),
      reportee,
      manager,
    });
    setConfirmOpen(true);
  };

  const confirmAssignment = async () => {
    if (!confirmMove) return;
    setConfirmSaving(true);
    try {
      const res = await assignManager({
        reportee_id: confirmMove.reportee_id,
        supervisor_id: confirmMove.supervisor_id,
        is_org_root: confirmMove.is_org_root,
      });
      if (res?.success) {
        toast.success(res?.message || 'Manager updated');
        await fetchUsers();
        window.dispatchEvent(
          new CustomEvent('reporting:changed', {
            detail: { reportee_id: confirmMove.reportee_id, supervisor_id: confirmMove.supervisor_id },
          })
        );
        setConfirmOpen(false);
        setConfirmMove(null);
      } else {
        toast.error(res?.message || res?.error_msg || 'Failed to update manager');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to update manager');
    } finally {
      setConfirmSaving(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handler = () => {
      fetchUsers();
    };
    window.addEventListener('reporting:changed', handler);
    return () => window.removeEventListener('reporting:changed', handler);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpenFor(null);
    };

    const onDocumentClick = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest('[data-org-menu="true"]')) return;
      setMenuOpenFor(null);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onDocumentClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onDocumentClick);
    };
  }, []);

  const { childrenBySupervisorId, roots, unassignedUsers } = useMemo(() => {
    const map = new Map();
    const childrenMap = new Map();

    users.forEach((u) => {
      map.set(String(u.id), u);
    });

    users.forEach((u) => {
      const supRaw = u.supervisor_id;
      const isNone = supRaw == null || supRaw === 0 || supRaw === '0' || supRaw === '';
      const sup = isNone ? null : String(supRaw);
      const key = sup;
      if (!childrenMap.has(key)) childrenMap.set(key, []);
      childrenMap.get(key).push(u);
    });

    for (const [, list] of childrenMap) {
      list.sort((a, b) => {
        const an = (a?.name || '').toString().toLowerCase();
        const bn = (b?.name || '').toString().toLowerCase();
        return an.localeCompare(bn);
      });
    }

    const rootCandidates = childrenMap.get(null) || [];

    const hasChildren = (id) => (childrenMap.get(String(id)) || []).length > 0;
    const hasReports = (u) => {
      const raw = u?.direct_reports_count;
      if (raw != null && raw !== '') return Number(raw) > 0;
      return hasChildren(u?.id);
    };

    const isPinnedRoot = (u) => Number(u?.is_org_root) === 1;

    const orphaned = users.filter((u) => {
      const supRaw = u.supervisor_id;
      const isNone = supRaw == null || supRaw === 0 || supRaw === '0' || supRaw === '';
      if (isNone) return false;
      return !map.has(String(supRaw));
    });

    const topLevelNoReports = rootCandidates.filter((u) => !hasReports(u));
    const topLevelHidden = topLevelNoReports.filter((u) => !isPinnedRoot(u));

    const unassignedMap = new Map();
    [...orphaned, ...topLevelHidden].forEach((u) => unassignedMap.set(String(u.id), u));
    const unassignedList = Array.from(unassignedMap.values()).sort((a, b) => {
      const an = (a?.name || '').toString().toLowerCase();
      const bn = (b?.name || '').toString().toLowerCase();
      return an.localeCompare(bn);
    });

    let rootList = rootCandidates.filter((u) => hasReports(u) || isPinnedRoot(u));
    if (rootList.length === 0 && rootCandidates.length > 0) {
      rootList = [rootCandidates[0]];
    }
    rootList = rootList.sort((a, b) => {
      const an = (a?.name || '').toString().toLowerCase();
      const bn = (b?.name || '').toString().toLowerCase();
      return an.localeCompare(bn);
    });

    return {
      childrenBySupervisorId: childrenMap,
      roots: rootList,
      unassignedUsers: unassignedList,
    };
  }, [users]);

  const clampZoom = (value) => Math.min(2, Math.max(0.55, value));
  const zoomIn = () => setZoom((z) => clampZoom(Number((z + 0.1).toFixed(2))));
  const zoomOut = () => setZoom((z) => clampZoom(Number((z - 0.1).toFixed(2))));
  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y });
  };

  const onMouseMove = (e) => {
    if (!isPanning) return;
    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;
    setPan({ x: panStart.panX + dx, y: panStart.panY + dy });
  };

  const onMouseUp = () => {
    setIsPanning(false);
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getInitials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const toAbsoluteUrl = (maybeUrl) => {
    if (!maybeUrl) return null;
    const str = String(maybeUrl).trim();
    if (!str) return null;
    if (/^https?:\/\//i.test(str)) return str;

    const base = (process.env.REACT_APP_API_BASE_URL || '').toString().replace(/\/+$/, '');
    if (!base) return str;
    const path = str.startsWith('/') ? str : `/${str}`;
    return `${base}${path}`;
  };

  const renderCard = (user, isRoot = false) => {
    const id = String(user.id);
    const children = childrenBySupervisorId.get(id) || [];
    const isExpanded = expanded.has(id);
    const employeeId = user?.employee_id ?? user?.emp_id ?? null;
    const profilePic = toAbsoluteUrl(user?.profile_pic_url);
    const isMenuOpen = String(menuOpenFor) === id;
    const canShowImg = Boolean(profilePic) && !imgFailed.has(id);

    return (
      <div
        className={`org-card ${isRoot ? 'org-card-root' : ''} ${String(dropHoverId) === id ? 'org-drop-hover' : ''}`}
        draggable
        onDragStart={(e) => {
          startDrag(user);
          try {
            e.dataTransfer.setData('text/plain', String(user.id));
            e.dataTransfer.effectAllowed = 'move';
          } catch {
            // no-op
          }
        }}
        onDragEnd={() => {
          setDraggingUser(null);
          setDropHoverId(null);
        }}
        onDragOver={(e) => {
          if (!draggingUser) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDropHoverId(id);
        }}
        onDragLeave={() => {
          setDropHoverId((prev) => (String(prev) === id ? null : prev));
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDropHoverId(null);
          const draggedId = draggingUser?.id || e.dataTransfer.getData('text/plain');
          if (!draggedId) return;
          requestMove({ reporteeId: draggedId, supervisorId: id });
        }}
      >
        <div className="org-card-inner">
          <div className={`org-avatar ${isRoot ? 'org-avatar-root' : ''}`}>
            {canShowImg ? (
              <img
                src={profilePic}
                alt={user.name}
                className="org-avatar-img"
                onError={() =>
                  setImgFailed((prev) => {
                    const next = new Set(prev);
                    next.add(id);
                    return next;
                  })
                }
              />
            ) : (
              <div className="org-avatar-text">{getInitials(user.name)}</div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="org-name" title={user.name}>
                  {user.name}
                </div>
                <div className="org-meta" title={user.email}>
                  {user.email}
                </div>
                <div className="org-submeta">
                  {employeeId ? `Emp ID - ${employeeId}` : `User ID - ${String(user.id).padStart(4, '0')}`}
                </div>
              </div>
              <div className="relative" data-org-menu="true">
                <button
                  type="button"
                  className="org-menu"
                  title="More"
                  onClick={() => setMenuOpenFor((prev) => (String(prev) === id ? null : id))}
                >
                  <Icon icon="fluent:more-vertical-24-regular" width={18} />
                </button>

                {isMenuOpen && (
                  <div className="org-menu-pop">
                    <button
                      type="button"
                      className="org-menu-item"
                      onClick={() => {
                        setMenuOpenFor(null);
                        window.open(`/admin/profile`, '_blank');
                      }}
                    >
                      <Icon icon="fluent:person-24-regular" width={18} />
                      <span>View profile</span>
                    </button>

                    <button
                      type="button"
                      className="org-menu-item"
                      onClick={() => {
                        setMenuOpenFor(null);
                        if (typeof onAssignManager === 'function') {
                          onAssignManager({ userId: user.id, managerId: user.supervisor_id });
                        }
                      }}
                    >
                      <Icon icon="fluent:person-arrow-right-24-regular" width={18} />
                      <span>Assign/Change manager</span>
                    </button>

                    <button
                      type="button"
                      className="org-menu-item"
                      onClick={() => {
                        setMenuOpenFor(null);
                        requestMove({ reporteeId: user.id, supervisorId: null, isOrgRoot: false });
                      }}
                    >
                      <Icon icon="fluent:person-delete-24-regular" width={18} />
                      <span>Remove from org chart</span>
                    </button>

                    {children.length > 0 && (
                      <button
                        type="button"
                        className="org-menu-item"
                        onClick={() => {
                          setMenuOpenFor(null);
                          toggleExpanded(id);
                        }}
                      >
                        <Icon icon={isExpanded ? 'fluent:subtract-square-24-regular' : 'fluent:add-square-24-regular'} width={18} />
                        <span>{isExpanded ? 'Collapse subtree' : 'Expand subtree'}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {children.length > 0 && (
          <div className="org-card-footer">
            <button type="button" className="org-toggle" onClick={() => toggleExpanded(id)}>
              <span className="org-count">{children.length}</span>
              <span className="org-toggle-text">Direct reports</span>
              <Icon icon={isExpanded ? 'fluent:chevron-up-24-regular' : 'fluent:chevron-down-24-regular'} width={18} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderNode = (user) => {
    const id = String(user.id);
    const children = childrenBySupervisorId.get(id) || [];
    const isExpanded = expanded.has(id);

    return (
      <li key={id}>
        {renderCard(user, false)}
        {children.length > 0 && isExpanded && (
          <ul>
            {children.map((child) => renderNode(child))}
          </ul>
        )}
      </li>
    );
  };

  const unassignedCount = unassignedUsers.length;
  const filteredUnassignedUsers = useMemo(() => {
    const q = (unassignedQuery || '').trim().toLowerCase();
    if (!q) return unassignedUsers;
    return unassignedUsers.filter((u) => {
      const name = (u?.name || '').toString().toLowerCase();
      const email = (u?.email || '').toString().toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [unassignedUsers, unassignedQuery]);

  const isMultiRoot = roots.length > 1;

  return (
    <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm p-6">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-black text-gray-900">Org Chart</h2>
          <p className="text-sm text-gray-500 font-medium">Visualize who reports to whom based on the manager assignments.</p>
        </div>

        <div className="flex items-center gap-2">
          {showAssignManagerButton && (
            <button
              type="button"
              onClick={() => (typeof onAssignManager === 'function' ? onAssignManager({}) : null)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-black shadow-sm"
              title="Assign manager"
            >
              <Icon icon="fluent:person-arrow-right-24-regular" width={18} />
              Assign Manager
            </button>
          )}
          <button
            type="button"
            onClick={openTopLevelModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-white border border-gray-100 text-gray-700 hover:bg-gray-50 text-sm font-black shadow-sm"
            title="Set top-level manager"
          >
            <Icon icon="fluent:crown-24-regular" width={18} className="text-[#7083FF]" />
            Top-Level
          </button>
          <button
            type="button"
            onClick={zoomOut}
            className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50"
            title="Zoom out"
          >
            <Icon icon="fluent:subtract-24-regular" width={18} />
          </button>
          <div className="px-3 py-2 rounded-2xl bg-gray-50 border border-gray-100 text-xs font-black text-gray-700 tabular-nums" title="Zoom">
            {Math.round(zoom * 100)}%
          </div>
          <button
            type="button"
            onClick={zoomIn}
            className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50"
            title="Zoom in"
          >
            <Icon icon="fluent:add-24-regular" width={18} />
          </button>
          <button
            type="button"
            onClick={resetView}
            className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50"
            title="Reset view"
          >
            <Icon icon="fluent:arrow-counterclockwise-24-regular" width={18} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden', transition: 'all 0.3s' }}>
            {roots.length === 0 ? (
              <div className="text-sm font-medium text-gray-500">No top-level users found. Assign at least one user with no manager.</div>
            ) : (
              <div className="org-wrap">
                <div className="org-scroll">
                  <div
                    className={`org-pan ${isPanning ? 'org-pan-active' : ''}`}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    role="presentation"
                    title="Drag to pan"
                  >
                    <div
                      className={`org-stage ${isMultiRoot ? 'org-stage-multi' : ''}`}
                      style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: 'top center',
                      }}
                    >
                      {roots.length <= 1 ? (
                        <ul className="org-tree">
                          {roots.map((root) => (
                            <li key={String(root.id)}>
                              {renderCard(root, true)}
                              {(childrenBySupervisorId.get(String(root.id)) || []).length > 0 && expanded.has(String(root.id)) && (
                                <ul>
                                  {(childrenBySupervisorId.get(String(root.id)) || []).map((child) => renderNode(child))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="org-multi-root">
                          {roots.map((root) => (
                            <div key={String(root.id)} className="org-root-block">
                              <ul className="org-tree org-tree-root">
                                <li>
                                  {renderCard(root, true)}
                                  {(childrenBySupervisorId.get(String(root.id)) || []).length > 0 && expanded.has(String(root.id)) && (
                                    <ul>
                                      {(childrenBySupervisorId.get(String(root.id)) || []).map((child) => renderNode(child))}
                                    </ul>
                                  )}
                                </li>
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {unassignedCount > 0 && (
            <div style={{ flexShrink: 0, position: 'sticky', top: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setSidebarOpen((s) => !s)}
                title={sidebarOpen ? 'Collapse sidebar' : 'Show unassigned users'}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '20px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: '#374151', whiteSpace: 'nowrap' }}
              >
                <Icon icon={sidebarOpen ? 'fluent:panel-right-contract-24-regular' : 'fluent:panel-right-expand-24-regular'} width={16} />
                {!sidebarOpen && <span>{unassignedCount} Unassigned</span>}
              </button>
              {sidebarOpen && (
              <div style={{ width: '280px' }}>
              <div className="border border-gray-100 rounded-[20px] bg-gray-50 p-4">
                <button
                  type="button"
                  onClick={() => setShowUnassigned((s) => !s)}
                  className="w-full flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center justify-center w-7 h-6 rounded-full bg-white border border-gray-100 text-xs font-black text-gray-700">
                      {unassignedCount}
                    </span>
                    <div className="text-sm font-black text-gray-900 truncate">Unassigned users</div>
                    <div className="text-xs font-medium text-gray-500 shrink-0">(not shown in chart)</div>
                  </div>
                  <Icon
                    icon={showUnassigned ? 'fluent:chevron-up-24-regular' : 'fluent:chevron-down-24-regular'}
                    width={18}
                    className="text-gray-600"
                  />
                </button>

                {showUnassigned && (
                  <div className="mt-4">
                    <div className="relative">
                      <Icon
                        icon="fluent:search-24-regular"
                        width={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        value={unassignedQuery}
                        onChange={(e) => setUnassignedQuery(e.target.value)}
                        placeholder="Search unassigned..."
                        className="w-full pl-10 pr-10 py-2 rounded-2xl bg-white border border-gray-100 text-sm font-medium text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                      />
                      {unassignedQuery && (
                        <button
                          type="button"
                          onClick={() => setUnassignedQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-8 h-8 rounded-2xl text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                          title="Clear"
                        >
                          <Icon icon="fluent:dismiss-24-regular" width={16} />
                        </button>
                      )}
                    </div>

                    <div className="mt-3 max-h-[480px] overflow-auto pr-1 space-y-2">
                      {filteredUnassignedUsers.length === 0 ? (
                        <div className="text-sm font-medium text-gray-500 px-2 py-6 text-center">No matches</div>
                      ) : (
                        filteredUnassignedUsers.map((u) => (
                          <div
                            key={u.id}
                            draggable
                            onDragStart={(e) => {
                              startDrag(u);
                              try {
                                e.dataTransfer.setData('text/plain', String(u.id));
                                e.dataTransfer.effectAllowed = 'move';
                              } catch {
                                // no-op
                              }
                            }}
                            onDragEnd={() => {
                              setDraggingUser(null);
                              setDropHoverId(null);
                            }}
                            className="w-full bg-white border border-gray-100 rounded-2xl px-4 py-3 flex items-center justify-between gap-3"
                            title="Drag onto a manager to assign"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-black text-gray-900 truncate">{u.name}</div>
                              <div className="text-xs font-medium text-gray-500 truncate">{u.email}</div>
                            </div>
                            <div className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-white border border-gray-100 text-gray-500" title="Drag">
                              <Icon icon="fluent:drag-24-regular" width={18} />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {confirmOpen && confirmMove && (
        <div className="fixed inset-0 z-[1000]">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => {
              if (confirmSaving) return;
              setConfirmOpen(false);
              setConfirmMove(null);
            }}
            role="presentation"
          />
          <div className="absolute left-1/2 top-1/2 w-[92vw] max-w-[520px] -translate-x-1/2 -translate-y-1/2 bg-white rounded-[24px] border border-gray-100 shadow-[0_30px_80px_rgba(0,0,0,0.25)] overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-black text-gray-900">Confirm change</div>
              <button
                type="button"
                onClick={() => {
                  if (confirmSaving) return;
                  setConfirmOpen(false);
                  setConfirmMove(null);
                }}
                className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50"
                title="Close"
              >
                <Icon icon="fluent:dismiss-24-regular" width={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm font-medium text-gray-700">
                Move <span className="font-black text-gray-900">{confirmMove.reportee?.name || 'this user'}</span>
              </div>
              <div className="text-sm font-medium text-gray-700">
                New manager:{' '}
                <span className="font-black text-gray-900">
                  {confirmMove.manager ? confirmMove.manager.name : 'Unassigned'}
                </span>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (confirmSaving) return;
                  setConfirmOpen(false);
                  setConfirmMove(null);
                }}
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAssignment}
                disabled={confirmSaving}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {confirmSaving ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon icon="fluent:checkmark-24-regular" width={16} />
                    Confirm
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {topLevelOpen && (
        <div className="fixed inset-0 z-[1000]">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setTopLevelOpen(false)}
            role="presentation"
          />
          <div className="absolute left-1/2 top-1/2 w-[92vw] max-w-[640px] -translate-x-1/2 -translate-y-1/2 bg-white rounded-[24px] border border-gray-100 shadow-[0_30px_80px_rgba(0,0,0,0.25)] overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <div className="text-sm font-black text-gray-900">Set top-level</div>
                <div className="text-xs font-medium text-gray-500">Select an employee to set as top-level (no manager).</div>
              </div>
              <button
                type="button"
                onClick={() => setTopLevelOpen(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50"
                title="Close"
              >
                <Icon icon="fluent:dismiss-24-regular" width={18} />
              </button>
            </div>

            <div className="p-5">
              <div className="relative">
                <Icon
                  icon="fluent:search-24-regular"
                  width={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  value={topLevelQuery}
                  onChange={(e) => setTopLevelQuery(e.target.value)}
                  placeholder="Search employee by name or email..."
                  className="w-full pl-10 pr-10 py-2 rounded-2xl bg-white border border-gray-200 text-sm font-medium text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                />
                {topLevelQuery && (
                  <button
                    type="button"
                    onClick={() => setTopLevelQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-8 h-8 rounded-2xl text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                    title="Clear"
                  >
                    <Icon icon="fluent:dismiss-24-regular" width={16} />
                  </button>
                )}
              </div>

              <div className="mt-4 max-h-[360px] overflow-auto pr-1 space-y-2">
                {topLevelCandidates.length === 0 ? (
                  <div className="text-sm font-medium text-gray-500 px-2 py-8 text-center">No matches</div>
                ) : (
                  topLevelCandidates.map((u) => {
                    const active = String(topLevelSelectedId) === String(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setTopLevelSelectedId(String(u.id))}
                        className={`w-full text-left border rounded-2xl px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
                          active
                            ? 'bg-indigo-50/60 border-indigo-200'
                            : 'bg-white border-gray-100 hover:bg-gray-50'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-black text-gray-900 truncate">{u.name}</div>
                          <div className="text-xs font-medium text-gray-500 truncate">{u.email}</div>
                        </div>
                        {active ? (
                          <Icon icon="fluent:checkmark-circle-24-filled" width={18} className="text-indigo-600" />
                        ) : (
                          <Icon icon="fluent:circle-24-regular" width={18} className="text-gray-300" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setTopLevelOpen(false)}
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!topLevelSelectedId) {
                    toast.warning('Please select an employee');
                    return;
                  }
                  setTopLevelOpen(false);
                  requestMove({ reporteeId: topLevelSelectedId, supervisorId: null, isOrgRoot: true });
                }}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-sm"
              >
                <Icon icon="fluent:add-24-regular" width={16} />
                Add
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default OrgChart;
